import { Cart, OfferStatus } from "Store/cart/cartInterfaces";
import {
  INSURANCE_NAME_VALUE,
  PARTNER_PRODUCTS_HIDE_BUY_MORE,
  PRODUCTS_HIDE_BUY_MORE,
  isLifeInsurance,
  isLifeOrChildInsurance,
  isPersonalInsurance,
  isRiskInsurance,
  isStorebrandHealthProviderInsurance,
  isStorebrandLifeProviderInsurance,
} from "Store/interfaces/products";
import {
  AkaCartPartnerTypes,
  CartPartnerTypes,
  Partner,
} from "Store/partners/partnerInterfaces";

import { sortProducts } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/utils";
import { isAdviceProductOnCart } from "Store/advice/utils";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { partnerFromAgreementCode } from "Store/partners/partnerUtils";
import { AgreementCode } from "Store/userIdentification/agreementCodes";

export const productNamesInCart = (cart: Cart): INSURANCE_NAME_VALUE[] =>
  cart.products.map((product) => product.name);

export const getActiveProducts = (cart: Cart): InsuranceProduct[] =>
  cart.products.filter((product) => product.visible);

export const getProductListsFromCart = (
  cart: Cart,
  entrance: INSURANCE_NAME_VALUE | null,
): {
  allProducts: InsuranceProduct[];
  activeProducts: InsuranceProduct[];
  specialProducts: InsuranceProduct[];
  toggledProducts: InsuranceProduct[];
  adviceProducts: InsuranceProduct[];
  sortedAdviceProducts: InsuranceProduct[];
} => {
  const allProducts = getActiveProducts(cart);
  const activeProducts = allProducts.filter(
    (product) =>
      product.name !== INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN &&
      !isAdviceProductOnCart(product, cart),
  );
  const specialProducts = allProducts.filter(
    (product) => product.name === INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN,
  );
  const toggledProducts = allProducts.filter(
    (product) =>
      isPartnerCart(cart) &&
      product.name === INSURANCE_NAME_VALUE.ELECTRIC_SCOOTER,
  );
  const adviceProducts = allProducts.filter((product) =>
    isAdviceProductOnCart(product, cart),
  );
  const sortedAdviceProducts = sortProducts(adviceProducts, {
    [entrance as INSURANCE_NAME_VALUE]: 0.1,
  });
  return {
    allProducts,
    activeProducts,
    specialProducts,
    toggledProducts,
    adviceProducts,
    sortedAdviceProducts,
  };
};

export const getActiveOrActiveUndefinedProducts = (
  cart: Cart,
): InsuranceProduct[] =>
  cart.products.filter(
    (product) => product.visible || product.visible === undefined,
  );

export const isSentOfferFromAdvisor = (cart: Cart): boolean =>
  !!cart.cartOwnedByAdvisor && cart.offerInfo?.status === OfferStatus.SENT;

export const isSentOrAcceptedOfferFromAdvisor = (cart: Cart | null): boolean =>
  !!cart &&
  !!cart.cartOwnedByAdvisor &&
  (cart.offerInfo?.status === OfferStatus.SENT ||
    cart.offerInfo?.status === OfferStatus.ACCEPTED);

export const isNotSentOfferFromAdvisor = (cart: Cart | null): boolean =>
  !!cart &&
  !!cart.cartOwnedByAdvisor &&
  cart.offerInfo?.status === OfferStatus.NOT_SENT;

export const offerAccepted = (cart: Cart): boolean =>
  cart.offerInfo?.status === OfferStatus.ACCEPTED;

export const activeOffer = (cart: Cart): boolean =>
  cart.offerInfo?.status === OfferStatus.SENT;

export const cartContainsRiskInsurance = (cart: Cart): boolean =>
  getActiveProducts(cart).some((product) => isRiskInsurance(product.name));

export const cartContainsLifeInsurance = (cart: Cart): boolean =>
  getActiveProducts(cart).some((product) => isLifeInsurance(product.name));

export const cartContainsLifeOrChildInsurance = (cart: Cart): boolean =>
  getActiveProducts(cart).some((product) =>
    isLifeOrChildInsurance(product.name),
  );

export const cartContainsPersonalInsurance = (cart: Cart): boolean =>
  getActiveProducts(cart).some((product) => isPersonalInsurance(product.name));

export const cartContainsOnlyRiskOrLifeProviderInsurances = (
  cart: Cart,
): boolean =>
  getActiveProducts(cart).every(
    (product) => !isStorebrandHealthProviderInsurance(product.name),
  );

export const cartContainsLifeProviderInsurances = (cart: Cart): boolean =>
  getActiveProducts(cart).some((product) =>
    isStorebrandLifeProviderInsurance(product.name),
  );

export const cartContainsOnlyHealthProviderInsurances = (cart: Cart): boolean =>
  getActiveProducts(cart).every((product) =>
    isStorebrandHealthProviderInsurance(product.name),
  );

export const cartContainsHealthProviderInsurances = (cart: Cart): boolean =>
  getActiveProducts(cart).some((product) =>
    isStorebrandHealthProviderInsurance(product.name),
  );

export const cartContainsChildHealthNotHealthInsurances = (
  cart: Cart,
): boolean =>
  getActiveProducts(cart).some(
    (product) => product.name === INSURANCE_NAME_VALUE.CHILD_HEALTH,
  ) &&
  !getActiveProducts(cart).some(
    (product) =>
      product.name !== INSURANCE_NAME_VALUE.CHILD_HEALTH &&
      isStorebrandHealthProviderInsurance(product.name),
  );

export const cartContainsHealthAndChildHealthInsurances = (
  cart: Cart,
): boolean =>
  getActiveProducts(cart).some(
    (product) => product.name === INSURANCE_NAME_VALUE.CHILD_HEALTH,
  ) &&
  getActiveProducts(cart).some(
    (product) =>
      product.name !== INSURANCE_NAME_VALUE.CHILD_HEALTH &&
      isStorebrandHealthProviderInsurance(product.name),
  );

const cartContainsOnlyElbilInsurances = (
  cart: Cart,
  forOnlyVisibleProducts = true,
): boolean =>
  (forOnlyVisibleProducts
    ? getActiveProducts(cart)
    : getActiveOrActiveUndefinedProducts(cart)
  ).every(
    (product) => product.name === INSURANCE_NAME_VALUE.CAR_NORSK_ELBILFORENING,
  );

export const cartContainsPregnantAndNewbornInsurance = (cart: Cart): boolean =>
  getActiveProducts(cart).some(
    (product) => product.name === INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN,
  );

const cartContainsSpecialInsurance = (
  cart: Cart,
  forOnlyVisibleProducts = true,
): boolean =>
  (forOnlyVisibleProducts
    ? getActiveProducts(cart)
    : getActiveOrActiveUndefinedProducts(cart)
  ).some(
    (product) =>
      product.name === INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE ||
      product.name === INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN,
  );

export const cartNeedsEDH = (cart: Cart): boolean =>
  getActiveProducts(cart).some(
    (product) => product.needsDeclarationOfHealth === true,
  );

export const cartHasCollectiveDiscount = (
  cart: Cart,
  forOnlyVisibleProducts = true,
): boolean =>
  (cart.agreementProduct === AgreementCode.PARENT_COMPANY_CUSTOMER_TARIFF ||
    cart.agreementProduct === AgreementCode.COLLECTIVE_TARIFF) &&
  !cart.containsLifeInsurancesOnly &&
  !cartContainsSpecialInsurance(cart, forOnlyVisibleProducts) &&
  !cartContainsOnlyElbilInsurances(cart, forOnlyVisibleProducts);

export const cartHasBenefits = (cart: Cart): boolean =>
  cart.agreementProduct === AgreementCode.FFA_PROGRAM_TARIFF &&
  !cart.containsLifeInsurancesOnly &&
  !cartContainsSpecialInsurance(cart) &&
  !cartContainsOnlyElbilInsurances(cart);

export const cartCanGetCollectiveDiscount = (cart: Cart): boolean =>
  !!(
    cart.agreementProduct &&
    cart.agreementProduct > AgreementCode.COLLECTIVE_TARIFF &&
    !cartContainsSpecialInsurance(cart)
  );

export const hideBuyMoreInsurancesProductLinks = (
  cart: Cart,
  isUpsellActive: boolean,
): boolean => {
  if (isUpsellActive) {
    return true;
  }

  const isPartner = isPartnerCart(cart);

  const productsFilter = isPartner
    ? [...PRODUCTS_HIDE_BUY_MORE, ...PARTNER_PRODUCTS_HIDE_BUY_MORE]
    : PRODUCTS_HIDE_BUY_MORE;

  return cart.products.some((product) => productsFilter.includes(product.name));
};

/**
 * A part of the "display discounts"-experiment done by the Growth-team.
 * Checks if a cart has any benefits/discounts and returns the corresponding id.
 */
export const getCartBenefitOrDiscountId = (cart: Cart): string | undefined => {
  if (cartHasBenefits(cart)) {
    return "cartHasBenefits";
  } else if (cartHasCollectiveDiscount(cart)) {
    return "cartHasCollectiveDiscount";
  } else if (cartCanGetCollectiveDiscount(cart)) {
    return "cartCanGetCollectiveDiscount";
  }
  return undefined;
};

export const getAkaPartnerFromCart = (
  cart: Cart | null,
): AkaCartPartnerTypes | undefined => {
  const agreementCode = cart?.agreementProduct || 0;
  if (agreementCode === 0 || !isAkademikerneCart(cart)) {
    return undefined;
  }
  return partnerFromAgreementCode(agreementCode) as AkaCartPartnerTypes;
};

export const isAkademikerneCart = (cart: Cart | null): boolean => {
  const agreementCode = cart?.agreementProduct || 0;
  return isAkademikerneAgreementCode(agreementCode);
};

export const isAkademikerneAgreementCode = (
  code: number | undefined,
): boolean => {
  const agreementCode = code || 0;
  return (
    (agreementCode >= 280 &&
      agreementCode <= 291 &&
      agreementCode !== 285 &&
      agreementCode !== 286) ||
    (agreementCode >= AgreementCode.AFAG &&
      agreementCode <= AgreementCode.SAMFUNNSOKONOMENE &&
      agreementCode !== 585 &&
      agreementCode !== 586)
  );
};

export const isCoopCart = (cart: Cart | null): boolean => {
  const agreementCode = cart?.agreementProduct || 0;
  return agreementCode === AgreementCode.COOP || agreementCode === 421;
};

export const isDanskeBankCart = (cart: Cart | null): boolean => {
  const agreementCode = cart?.agreementProduct || 0;
  return agreementCode === AgreementCode.DANSKE_BANK || agreementCode === 431;
};

export const isHuseierneCart = (cart: Cart | null): boolean => {
  const agreementCode = cart?.agreementProduct || 0;
  return isHuseierneAgreementCode(agreementCode);
};

export const isHuseierneAgreementCode = (code: number | undefined): boolean => {
  return code === AgreementCode.HUSEIERNE || code === 411;
};

export const isPartnerCart = (cart: Cart | null): boolean => {
  return (
    isAkademikerneCart(cart) ||
    isCoopCart(cart) ||
    isDanskeBankCart(cart) ||
    isHuseierneCart(cart)
  );
};

export const getCartPartner = (cart: Cart): CartPartnerTypes => {
  if (isAkademikerneCart(cart)) {
    return getAkaPartnerFromCart(cart);
  }
  if (isCoopCart(cart)) {
    return Partner.COOP;
  }
  if (isDanskeBankCart(cart)) {
    return Partner.DANSKE_BANK;
  }
  if (isHuseierneCart(cart)) {
    return Partner.HUSEIERNE;
  }
  return undefined;
};
